import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios';
import { attachAuthInterceptors } from '../utils/axiosAuth';

// Define types for the API
export interface AssessmentData {
  patientName: string;
  facilityName: string;
  dateOfBirth: string;
  admissionDate: string;
  lengthOfStay: string;
  acuteAdmission: string;
  comorbidities: string[];
  emergencyVisits: string;
  assessedBy: string;
  notes?: string;
}

export interface Assessment extends AssessmentData {
  id: string;
  totalScore: number;
  readmissionProbability: string;
  lengthOfStayPoints: number;
  acuteAdmissionPoints: number;
  comorbidityPoints: number;
  emergencyVisitsPoints: number;
  createdAt: string;
  updatedAt: string;
}

export interface AssessmentFilters {
  patientName?: string;
  facilityName?: string;
  assessedBy?: string;
  dateFrom?: string;
  dateTo?: string;
  limit?: number;
  offset?: number;
}

export interface Statistics {
  overall: {
    totalAssessments: number;
    averageScore: number;
    minScore: number;
    maxScore: number;
    lowReadmission: number;
    moderateReadmission: number;
    highReadmission: number;
  };
  topFacilities: Array<{
    _id: string;
    count: number;
    averageScore: number;
  }>;
}

export interface HealthCheck {
  status: string;
  timestamp: string;
  version: string;
}

// Audit Log Types
export interface AuditLog {
  id: string;
  timestamp: string;
  userId: string;
  userEmail?: string;
  userName: string;
  action: 'CREATE' | 'READ' | 'UPDATE' | 'DELETE' | 'LOGIN' | 'LOGOUT' | 'LOGIN_FAILED' | 'DATA_EXPORT' | 'DATA_IMPORT' | 'SYSTEM_ERROR';
  resource: 'ASSESSMENT' | 'USER' | 'AUTHENTICATION' | 'REPORTS' | 'SYSTEM' | 'AUDIT_LOG';
  resourceId?: string;
  status: 'SUCCESS' | 'ERROR' | 'WARNING' | 'INFO';
  details: {
    patientName?: string;
    facilityName?: string;
    score?: number;
    ipAddress?: string;
    userAgent?: string;
    changes?: Record<string, any>;
    errorMessage?: string;
    requestBody?: Record<string, any>;
    responseStatus?: number;
  };
  createdAt: string;
  updatedAt: string;
}

export interface AuditLogFilters {
  page?: number;
  limit?: number;
  userId?: string;
  userName?: string;
  action?: string;
  resource?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// Reports Types
export interface ReportFilters {
  facilityName?: string;
  doctorName?: string;
  patientNumber?: string;
  startDate?: string;
  endDate?: string;
  reportType?: 'summary' | 'detailed';
}

export interface ReportData {
  type: 'summary' | 'detailed';
  totalRecords: number;
  data: any;
  summary?: {
    totalAssessments: number;
    averageScore: number;
    averageReadmissionProbability?: number;
    lowRisk: number;
    moderateRisk: number;
    highRisk: number;
  };
}

export interface ReportResponse {
  success: boolean;
  message: string;
  data: ReportData;
  filters: ReportFilters;
}

export interface ReportDownloadResponse {
  success: boolean;
  message: string;
  data: {
    filename: string;
    contentType: string;
    content: string;
    recordCount: number;
  };
}

export interface Facility {
  id: string;
  name: string;
  type: string;
  location: string;
}

export interface HealthCareProvider {
  name: string;
}

// Create axios instance with base configuration
const api: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:5000/api',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Log the API base URL for debugging
console.log('API Base URL:', import.meta.env.VITE_API_URL || 'http://localhost:5000/api');

api.interceptors.request.use(
  (config) => {
    console.log('API Request:', config.method?.toUpperCase(), config.url);
    return config;
  },
  (error: AxiosError) => {
    console.error('Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for handling common errors
api.interceptors.response.use(
  (response: AxiosResponse) => {
    console.log('API Response:', response.status, response.config.url);
    return response;
  },
  (error: AxiosError) => {
    if (error.response) {
      // Server responded with error status
      const { status, data, config } = error.response;
      console.error(`API Error ${status}:`, {
        url: config?.url,
        method: config?.method,
        message: (data as any)?.message,
        data: data
      });
      
      switch (status) {
        case 400:
          console.error('Bad Request:', (data as any)?.message);
          break;
        case 401:
          console.error('Unauthorized:', (data as any)?.message);
          // Handle unauthorized access
          break;
        case 404:
          console.error('Not Found:', (data as any)?.message);
          break;
        case 500:
          console.error('Server Error:', (data as any)?.message);
          break;
        default:
          console.error('API Error:', (data as any)?.message);
      }
    } else if (error.request) {
      // Network error
      console.error('Network Error:', {
        message: 'Unable to connect to server',
        url: error.config?.url,
        baseURL: error.config?.baseURL
      });
    } else {
      // Other error
      console.error('Request Setup Error:', error.message);
    }
    
    return Promise.reject(error);
  }
);

attachAuthInterceptors(api);

// API service functions
export const laceToolAPI = {
  // Create a new LACE tool assessment
  createAssessment: async (assessmentData: AssessmentData): Promise<Assessment> => {
    try {
      const response = await api.post('/lace-tool/assessments', assessmentData);
      // Transform the backend response format to match frontend expectations
      return response.data.data;
    } catch (error) {
      throw error;
    }
  },

  // Get all assessments with optional filters
  getAssessments: async (params: AssessmentFilters = {}): Promise<{ assessments: Assessment[]; total: number }> => {
    try {
      const response = await api.get('/lace-tool/assessments', { params });
      // Transform the backend response format to match frontend expectations
      return {
        assessments: response.data.data || [],
        total: response.data.pagination?.totalItems || 0
      };
    } catch (error) {
      throw error;
    }
  },

  // Get a specific assessment by ID
  getAssessment: async (id: string): Promise<Assessment> => {
    try {
      const response = await api.get(`/lace-tool/assessments/${id}`);
      // Transform the backend response format to match frontend expectations
      return response.data.data;
    } catch (error) {
      throw error;
    }
  },

  // Update an assessment
  updateAssessment: async (id: string, assessmentData: Partial<AssessmentData>): Promise<Assessment> => {
    try {
      const response = await api.put(`/lace-tool/assessments/${id}`, assessmentData);
      // Transform the backend response format to match frontend expectations
      return response.data.data;
    } catch (error) {
      throw error;
    }
  },

  // Delete an assessment
  deleteAssessment: async (id: string): Promise<{ message: string }> => {
    try {
      const response = await api.delete(`/lace-tool/assessments/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get assessment statistics
  getStatistics: async (): Promise<{ data: Statistics }> => {
    try {
      const response = await api.get('/lace-tool/statistics');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Health check
  healthCheck: async (): Promise<HealthCheck> => {
    try {
      const response = await api.get('/health');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Download assessment as PDF
  downloadAssessmentPDF: async (assessmentId: string): Promise<Blob> => {
    try {
      const response = await api.get(`/lace-tool/assessments/${assessmentId}/pdf`, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

// Audit Log API service functions
export const auditLogAPI = {
  // Get audit logs with filtering and pagination
  getAuditLogs: async (params: AuditLogFilters = {}): Promise<{ logs: AuditLog[]; total: number; pagination: any }> => {
    try {
      const response = await api.get('/audit-logs', { params });
      return {
        logs: response.data.data || [],
        total: response.data.pagination?.totalItems || 0,
        pagination: response.data.pagination
      };
    } catch (error) {
      throw error;
    }
  },

  // Get audit log statistics
  getAuditLogStats: async (params: { startDate?: string; endDate?: string } = {}): Promise<any> => {
    try {
      const response = await api.get('/audit-logs/stats', { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Export audit logs
  exportAuditLogs: async (params: { startDate?: string; endDate?: string; format?: string } = {}): Promise<Blob> => {
    try {
      const response = await api.get('/audit-logs/export', { 
        params,
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

// Reports API service functions
export const reportsAPI = {
  // Generate assessment reports with filtering
  generateReport: async (filters: ReportFilters = {}): Promise<ReportResponse> => {
    try {
      const response = await api.get('/reports/assessments', { params: filters });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Download report data as CSV/Excel
  downloadReport: async (filters: ReportFilters & { format?: string } = {}): Promise<ReportDownloadResponse> => {
    try {
      const response = await api.get('/reports/download', { params: filters });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get facilities list for filter dropdown
  getFacilities: async (): Promise<{ success: boolean; data: Facility[] }> => {
    try {
      const response = await api.get('/reports/facilities');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get health care providers list for filter dropdown
  getDoctors: async (): Promise<{ success: boolean; data: HealthCareProvider[] }> => {
    try {
      const response = await api.get('/reports/doctors');
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

export default api;
